/**
 * About CV PDF — 세로(Portrait) A4.
 * 폰트·Career 행 렌더는 cvPdfShared 참고.
 */

import React from 'react';
import { Document, Page, View, StyleSheet, Image } from '@react-pdf/renderer';
import { AboutContentMode, CareerType } from '../../shared/enum/EnumRepository';
import { CareerData, MemberInfoData } from '../../shared/dto/EntityRepository';
import { buildCvCareerBlocks, CvPdfText } from './cvPdfShared';
import { getCareerCurrentLabel } from '../../shared/util/careerCurrentLabel';
import logo from '../../asset/gnb/logo_qr_transparent.png';

const CV_INFORMATION_CONFIRM_EN =
  'I confirm that all the information provided above is accurate to the best of my knowledge.';

interface DocumentVerticalCVProps {
  memberInfo: MemberInfoData;
  careerList: CareerData[];
  careerSectionOrder?: readonly CareerType[];
  /** 기본 Orig(원문). ENG면 nameEn·descriptionEn·contentEn 사용 */
  contentMode?: AboutContentMode;
}

const DocumentVerticalCV: React.FC<DocumentVerticalCVProps> = ({
  memberInfo,
  careerList,
  careerSectionOrder,
  contentMode = AboutContentMode.Orig,
}) => {
  const isEng = contentMode === AboutContentMode.ENG;
  const displayName = isEng ? memberInfo.nameEn ?? '' : memberInfo.name;
  const displayDescription = isEng
    ? memberInfo.descriptionEn ?? ''
    : memberInfo.description;
  const careerText = (c: CareerData) =>
    isEng ? c.contentEn ?? '' : c.content ?? '';

  const pdfStyles = { Section: PDFStyles.Section, Text: PDFStyles.Text };

  return (
    <Document>
      <Page size="A4" style={PDFStyles.Page.root}>
        <View style={PDFStyles.Section.root}>
          <View style={PDFStyles.Section.profileAndInfo}>
            <View style={PDFStyles.Section.infoContainer}>
              <View style={PDFStyles.Section.nameRow}>
                <CvPdfText style={PDFStyles.Text.name}>{displayName}</CvPdfText>
                <CvPdfText style={PDFStyles.Text.inlineMeta}>
                  {` (b. ${memberInfo.year}, ${memberInfo.country})`}
                </CvPdfText>
              </View>
            </View>
          </View>

          <View style={PDFStyles.Section.description}>
            <CvPdfText style={PDFStyles.Text.description}>{displayDescription}</CvPdfText>
          </View>

          {careerList && (
            <View style={PDFStyles.Section.careerContainer}>
              {buildCvCareerBlocks(
                careerList,
                careerText,
                pdfStyles,
                careerSectionOrder,
                (career) =>
                  getCareerCurrentLabel(career.currentLabelOrig, contentMode),
              )}
            </View>
          )}
        </View>
        {/* fixed: 이 Document를 이루는 모든 장에 로고(본문이 여러 페이지로 이어질 때 동일) */}
        <View style={PDFStyles.Section.pageLogo} fixed>
          <Image source={logo} style={PDFStyles.Image.logo} cache />
        </View>
        {/* 쪽번호: wrap으로 장이 나뉠 때 매 장에 표시하려면 fixed 필요. 확인 문구는 문서 마지막 장만 */}
        <View style={PDFStyles.Section.informationConfirm} fixed>
          <CvPdfText
            style={PDFStyles.Text.informationConfirm}
            render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
              pageNumber === totalPages ? CV_INFORMATION_CONFIRM_EN : ''
            }
          />
        </View>
        <View style={PDFStyles.Section.pageNumber} fixed>
          <CvPdfText
            style={PDFStyles.Text.pageNumber}
            render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
              `- ${pageNumber} / ${totalPages} -`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

const PDFTypography = {
  H1: {
    fontSize: 14,
    fontFamily: 'NotoKR',
    fontWeight: 600,
  },
  InlineMemberInfo: {
    fontSize: 9,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    color: '#6B6B6B',
  },
  MemberInfoBodySmall: {
    fontSize: 8,
    fontFamily: 'NotoKR',
    fontWeight: 400,
  },
  CareerTitle: {
    fontSize: 10,
    fontFamily: 'NotoKR',
    fontWeight: 500,
  },
  CareerBody: {
    fontSize: 9,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    lineHeight: 1.3,
  },
  PageNumber: {
    fontSize: 7,
    fontFamily: 'NotoKR',
    fontWeight: 400,
  },
  InformationConfirm: {
    fontSize: 7,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    color: 'lightgray',
  },
};

const PDFStyles = {
  Page: StyleSheet.create({
    root: {
      paddingTop: 50,
      paddingBottom: 70,
      paddingLeft: 35,
      paddingRight: 35,
      backgroundColor: '#ffffff',
      fontFamily: 'NotoKR',
      fontWeight: 400,
    },
  }),

  Image: StyleSheet.create({
    logo: {
      width: 24,
      height: 24,
      objectFit: 'contain' as const,
    },
  }),

  Section: StyleSheet.create({
    root: {},
    profileAndInfo: {
      marginBottom: 18,
    },
    infoContainer: {
      width: '100%',
    },
    nameRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'flex-end',
      marginBottom: 10,
    },
    description: {
      width: '80%',
    },
    careerContainer: {
      marginTop: 24,
      alignSelf: 'flex-start',
      width: '100%',
    },
    careerItem: {
      flexDirection: 'row',
      marginBottom: 4,
    },
    careerYearCol: {
      width: 76,
      flexDirection: 'row',
    },
    careerContentWrap: {
      flex: 1,
      minWidth: 0,
    },
    pageLogo: {
      position: 'absolute',
      top: 5,
      right: 16,
    },
    informationConfirm: {
      position: 'absolute',
      bottom: 50,
      right: 30,
      textAlign: 'right',
    },
    pageNumber: {
      position: 'absolute',
      bottom: 30,
      left: 0,
      right: 0,
      textAlign: 'center',
    },
  }),

  Text: StyleSheet.create({
    name: {
      ...PDFTypography.H1,
    },
    inlineMeta: {
      ...PDFTypography.InlineMemberInfo,
      marginLeft: 4,
    },
    description: {
      ...PDFTypography.MemberInfoBodySmall,
      lineHeight: 1.5,
    },
    careerTitle: {
      ...PDFTypography.CareerTitle,
      width: '60%',
      marginBottom: 4,
    },
    careerTitleSectionGap: {
      marginTop: 20,
    },
    careerYear: {
      ...PDFTypography.CareerBody,
    },
    careerYearStart: {
      ...PDFTypography.CareerBody,
      width: 26,
    },
    separator: {
      ...PDFTypography.CareerBody,
      width: 8,
    },
    careerYearEnd: {
      ...PDFTypography.CareerBody,
      width: 38,
    },
    careerContent: {
      ...PDFTypography.CareerBody,
    },
    pageNumber: {
      ...PDFTypography.PageNumber,
    },
    informationConfirm: {
      ...PDFTypography.InformationConfirm,
    },
  }),
};

export default DocumentVerticalCV;
